/**
 * SDMS Template Builder — the data-transfer sheet editor (web twin of the Windows builder's
 * frmSpreadSheetEditor + frmYamlFile), opened by View on a Data Transfer row.
 *
 *  - the workbook opens in the same editable Univer designer as the template sheet;
 *  - **Upload Yaml File** stores the instrument-parser YAML on the row and writes the columns it
 *    produces into row 1 of the active sheet, bold (legacy PopulateHeadersFromYaml);
 *  - **YAML** previews / removes the stored or uploaded YAML (legacy frmYamlFile);
 *  - **Load Instrument File** parses a sample file with that YAML — with the SAME engine Result
 *    Entry imports with — and replaces the active sheet: header in row 1, row 2 left empty for the
 *    parsing mapping, data from row 3 as a reference. Those data rows are cleared again on Save
 *    (legacy StripInstrumentDataBeforeSave), so only rows 1–2 are kept.
 *
 * The legacy editor opens in Mail Merge design view over `RawDataTableDataSource` — the template's
 * Field Info fields (GenerateTransposedTable). The same here: the **field list** on the left is
 * those fields; drag one onto a cell (or select a cell and double-click / Enter) to write
 * `=FIELD("NAME")` there, and **Set Header** / **Set Detail** turn the selection into the
 * HEADERRANGE / DETAILRANGE mail-merge bands (the defined names the template sheet uses too).
 *
 * Save stores the sheet edits + YAML on the row straight away (audited on the template) — like the
 * report designer's Save. The Windows editor instead parks them until the template's Save; here
 * the data-transfer workbook is its own document, so it saves itself.
 */
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { useConfirm, useSaveFlow } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import { runLoading } from '../../../components/loading';
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { EMPTY_EDITS, isEmptyEdits } from './sheetEdits';
import { errorText, sdmsTemplateApi, toBase64, type BandChange, type BandName } from './sdmsTemplateApi';
import { a1, BAND_LABEL, effectiveBands, fieldFormula, readTemplateWorkbook, type WorkbookInfo } from './templateWorkbook';
import { FIELD_DRAG_MIME, type TemplateSheetHandle } from './TemplateSheetDesigner';

const TemplateSheetDesigner = lazy(() => import('./TemplateSheetDesigner').then(m => ({ default: m.TemplateSheetDesigner })));

/** Rows 3+ hold instrument data only while designing; row index 2 (0-based) is the first. */
const FIRST_DATA_ROW = 2;

export interface DataTransferTarget { uqID: number; name: string; hasFile: boolean; hasYaml: boolean }
/** A column of the mail-merge data source (`RawDataTableDataSource`). */
export interface DataSourceField { name: string; caption: string | null }

/** The two bands a data-transfer sheet carries (legacy Mail Merge → Header / Detail). */
const TRANSFER_BANDS = ['HEADERRANGE', 'DETAILRANGE'] as const;
const FIELD_RE = /^=?\s*FIELD\(\s*"([^"]+)"\s*\)\s*$/i;

export function DataTransferEditorDialog({ templateId, target, editable, formName, fields, onClose, onSaved, legacy = false, calibrationFields = [] }: {
  templateId: number;
  /** The row being edited; null = closed. */
  target: DataTransferTarget | null;
  editable: boolean;
  formName: string;
  /** The data source's columns — the template's Field Info. */
  fields: DataSourceField[];
  onClose: () => void;
  /** After a successful save (the tab reloads its lists). */
  onSaved: () => void;
  /** The legacy (G5) popup, used by Settings ▸ SDMS ▸ SDMS Template Builder: a Name box and a Mail
   *  Merge box (Header / Detail / CHeader / CDetail Range) above the sheet, and two field lists —
   *  RawData and CalibrationData — beside it. Default false: the Toolbox popup is unchanged. */
  legacy?: boolean;
  /** Legacy only: the CalibrationData list (`fields` is then the RawData list). */
  calibrationFields?: DataSourceField[];
}) {
  const notify = useNotify();
  const { confirm, alert } = useConfirm();
  const runSave = useSaveFlow();
  const sheetRef = useRef<TemplateSheetHandle | null>(null);
  const yamlInput = useRef<HTMLInputElement | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [book, setBook] = useState<{ buf: ArrayBuffer; info: WorkbookInfo } | null>(null);
  const [ready, setReady] = useState(false);
  /** The YAML in force: stored bytes, a new upload, or null (none / cleared). */
  const [yaml, setYaml] = useState<{ bytes: ArrayBuffer; fileName: string | null } | null>(null);
  const [yamlChanged, setYamlChanged] = useState(false);
  const [showYaml, setShowYaml] = useState(false);
  /** The sheet instrument data was loaded into — its rows 3+ are cleared on Save. */
  const [instrumentSheet, setInstrumentSheet] = useState<string | null>(null);
  /** Pending Header / Detail band changes (saved as defined names). */
  const [bands, setBands] = useState<BandChange[]>([]);
  /** Fields dropped this session (the workbook's own FIELD cells come from `book.info`). */
  const [dropped, setDropped] = useState<string[]>([]);
  const [fieldSearch, setFieldSearch] = useState('');
  /** Legacy popup: the Name box (a new data transfer is named here, not in the grid). */
  const [name, setName] = useState('');

  useEffect(() => {
    setBands([]); setDropped([]); setFieldSearch(''); setName(target?.name ?? '');
    if (!target) { setBook(null); setReady(false); setYaml(null); setYamlChanged(false); setInstrumentSheet(null); setShowYaml(false); return; }
    let alive = true;
    void (async () => {
      try {
        const buf = target.hasFile && target.uqID > 0
          ? await (await sdmsTemplateApi.outputFile(templateId, 'transfer', target.uqID)).arrayBuffer()
          : await sdmsTemplateApi.blankWorkbook();
        const y = target.hasYaml && target.uqID > 0 ? await sdmsTemplateApi.transferYaml(templateId, target.uqID) : null;
        if (!alive) return;
        setBook({ buf, info: await readTemplateWorkbook(buf) });
        setYaml(y ? { bytes: y, fileName: null } : null);
      } catch (e) {
        notify.error(errorText(e, 'Could not open the data transfer.'));
        onClose();
      }
    })();
    return () => { alive = false; };
    // Re-open only when another row is chosen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.uqID, target?.name, templateId]);

  const nameChanged = legacy && target != null && name.trim() !== (target.name ?? '').trim();
  const dirty = () => nameChanged || yamlChanged || instrumentSheet != null || bands.length > 0
    || (ready && !isEmptyEdits(sheetRef.current?.getEdits() ?? EMPTY_EDITS));
  useUnsavedGuard(target != null && editable && (nameChanged || yamlChanged || instrumentSheet != null || bands.length > 0 || dropped.length > 0));

  // ── data source field list + bands (legacy Mail Merge design view) ───────
  const info = book?.info;
  const allBands = useMemo(() => (info ? effectiveBands(info, bands) : []), [info, bands]);
  const mapped = useMemo(() => {
    const s = new Set(dropped);
    for (const f of info?.formulas ?? []) { const m = FIELD_RE.exec(f.formula); if (m?.[1]) s.add(m[1].toUpperCase()); }
    return s;
  }, [info, dropped]);
  const visibleFields = useMemo(() => {
    const q = fieldSearch.trim().toLowerCase();
    return fields.filter(f => !q || `${f.name} ${f.caption ?? ''}`.toLowerCase().includes(q));
  }, [fields, fieldSearch]);
  const sheetName = (i: number) => info?.sheets[i - 1] ?? `Sheet${i}`;

  const needSelection = () => {
    const r = sheetRef.current?.getActiveRange();
    if (!r) void alert({ title: 'Data Transfer', message: 'Select a cell or range on the sheet first.', tone: 'warning' });
    return r ?? null;
  };
  const putField = (name: string, at: { sheetIndex: number; row: number; col: number }) => {
    if (!sheetRef.current?.setCellFormula(at.sheetIndex, at.row, at.col, fieldFormula(name))) return;
    setDropped(d => (d.includes(name) ? d : [...d, name]));
    notify.info(`${name} mapped to ${sheetName(at.sheetIndex)}!${a1(at.row, at.col)}.`);
  };
  const putFieldAtSelection = (name: string) => {
    const r = needSelection();
    if (r) putField(name, { sheetIndex: r.sheetIndex, row: r.startRow, col: r.startCol });
  };
  const setBandFromSelection = (name: (typeof TRANSFER_BANDS)[number]) => {
    const r = needSelection();
    if (!r) return;
    const range = `${a1(r.startRow, r.startCol)}:${a1(r.endRow, r.endCol)}`;
    setBands(b => [...b.filter(x => !(x.sheetIndex === r.sheetIndex && x.name === name)), { sheetIndex: r.sheetIndex, name, range }]);
    notify.info(`${BAND_LABEL[name]} band set to ${sheetName(r.sheetIndex)}!${range}.`);
  };
  const clearBand = (sheetIndex: number, name: (typeof TRANSFER_BANDS)[number]) =>
    setBands(b => [...b.filter(x => !(x.sheetIndex === sheetIndex && x.name === name)), { sheetIndex, name, range: null }]);

  /** Legacy Mail Merge box: a range typed for the first sheet (empty clears the band). */
  const rangeOf = (band: BandName) => allBands.find(b => b.sheetIndex === 1 && b.name === band)?.range ?? '';
  const typeRange = (band: BandName, value: string) => {
    const range = value.replace(/\$/g, '').trim().toUpperCase();
    setBands(b => [...b.filter(x => !(x.sheetIndex === 1 && x.name === band)), { sheetIndex: 1, name: band, range: range || null }]);
  };

  const close = async () => {
    if (editable && dirty() && !(await confirm({ title: 'Data Transfer', message: 'Unsaved changes will be lost. Do you want to proceed?' }))) return;
    onClose();
  };

  const uploadYaml = async (file: File | undefined) => {
    if (!file) return;
    const bytes = await file.arrayBuffer();
    try {
      const headers = await runLoading(sdmsTemplateApi.yamlColumns(toBase64(bytes)), { label: 'Reading the YAML…' });
      setYaml({ bytes, fileName: file.name });
      setYamlChanged(true);
      if (headers.length > 0 && sheetRef.current?.writeHeaderRow(headers))
        notify.success(`YAML uploaded — ${headers.length} column header(s) written into row 1.`);
      else notify.success('YAML uploaded.');
    } catch (e) {
      notify.error(errorText(e, 'Could not read the headers from the YAML.'));
    }
  };

  const removeYaml = async () => {
    if (!(await confirm({ title: 'YAML File', message: 'Delete the uploaded YAML file?', tone: 'danger' }))) return;
    setYaml(null);
    setYamlChanged(true);
    setShowYaml(false);
  };

  const loadInstrumentFile = async (file: File | undefined) => {
    if (!file) return;
    if (!yaml) { await alert({ title: 'YAML required', message: 'Please upload a YAML for this data transfer first (Upload Yaml File).', tone: 'info' }); return; }
    if (!(await confirm({ title: 'Load Instrument File',
      message: 'The active sheet is replaced: header in row 1, row 2 left empty for the parsing mapping, the file\'s data from row 3 as a reference (cleared again on Save). Continue?' }))) return;
    try {
      const parsed = await runLoading(
        sdmsTemplateApi.yamlParse(toBase64(yaml.bytes), toBase64(await file.arrayBuffer()), file.name),
        { blocking: true, label: 'Parsing the instrument file…' });
      const sheet = sheetRef.current?.loadTable(parsed.columns, parsed.rows);
      if (!sheet) { notify.error('The instrument file could not be loaded into the sheet.'); return; }
      setInstrumentSheet(sheet);
      const shown = parsed.rows.length < parsed.totalRows ? ` (first ${parsed.rows.length} of ${parsed.totalRows} shown)` : '';
      const msg = `Loaded ${parsed.totalRows} row(s) and ${parsed.columns.length} column(s)${shown} — header in row 1, parsing row 2 left empty.`;
      if (parsed.warnings.length > 0) await alert({ title: 'Instrument file loaded', tone: 'warning', message: `${msg}\n\nWarnings:\n - ${parsed.warnings.join('\n - ')}` });
      else notify.success(msg);
    } catch (e) {
      notify.error(errorText(e, 'Could not load the instrument file.'));
    }
  };

  const save = async () => {
    if (!target || !sheetRef.current) return;
    const sheet = sheetRef.current;
    const saveName = legacy ? name.trim() : target.name;
    if (legacy && !saveName) { await alert({ title: 'Data Transfer', message: 'Name must not be empty.', tone: 'warning' }); return; }
    if (legacy && saveName.length > 50) { await alert({ title: 'Data Transfer', message: 'Name must be 50 characters or fewer.', tone: 'warning' }); return; }
    await runSave({
      formName,
      auditAction: target.uqID > 0 ? 'edit' : 'create',
      save: async () => {
        if (instrumentSheet) sheet.clearFromRow(instrumentSheet, FIRST_DATA_ROW);   // keep header + parsing row only
        const edits = sheet.getEdits();
        await sdmsTemplateApi.saveOutputs(templateId, {
          transfers: [{
            uqID: target.uqID, name: saveName,
            sheetEdits: isEmptyEdits(edits) ? undefined : edits,
            ...(yamlChanged ? (yaml ? { yamlBase64: toBase64(yaml.bytes) } : { clearYaml: true }) : {}),
            ...(bands.length > 0 ? { bands } : {}),
          }],
          reports: [],
        });
      },
      onError: e => notify.error(errorText(e, 'Save failed.')),
      onSaved: () => { onSaved(); onClose(); },
    });
  };

  const yamlText = yaml && showYaml ? new TextDecoder().decode(yaml.bytes) : '';

  return (
    <AppDialog open={target != null} title={legacy ? 'Data Transfer' : `Data Transfer — ${target?.name ?? ''}`} icon="ti-table-export" width={1200}
      onClose={() => void close()} submitOnEnter={false}
      footer={<>
        <button type="button" className="ca-btn" onClick={() => void close()}>{editable ? 'Cancel' : 'Close'}</button>
        {editable && <button type="button" className="ca-btn ca-btn-solid" disabled={!ready} onClick={() => void save()}>
          <i className="ti ti-device-floppy" /> Save</button>}
      </>}>
      <div className="stb-actions" data-unsaved-ignore>
        <button type="button" className="ca-btn" disabled={!editable || !ready} onClick={() => yamlInput.current?.click()}>
          <i className="ti ti-file-upload" /> Upload Yaml File</button>
        <button type="button" className="ca-btn" disabled={!yaml} onClick={() => setShowYaml(v => !v)}>
          <i className="ti ti-file-code" /> {showYaml ? 'Hide YAML' : 'YAML'}</button>
        <button type="button" className="ca-btn" disabled={!editable || !yaml} onClick={() => void removeYaml()}>
          <i className="ti ti-trash" /> Delete YAML</button>
        <button type="button" className="ca-btn" disabled={!editable || !ready} onClick={() => fileInput.current?.click()}>
          <i className="ti ti-file-import" /> Load Instrument File</button>
        <span className="stb-hint">
          {yaml ? `YAML: ${yaml.fileName ?? 'stored'} (${yaml.bytes.byteLength} bytes)${yamlChanged ? ' — saved on Save' : ''}` : 'No YAML uploaded for this data transfer.'}
          {instrumentSheet && ' · Instrument data rows (row 3 down) are removed on Save.'}
        </span>
        <input ref={yamlInput} type="file" accept=".yaml,.yml" hidden onChange={e => { void uploadYaml(e.target.files?.[0]); e.target.value = ''; }} />
        <input ref={fileInput} type="file" accept=".csv,.txt,.xlsx,.xls" hidden onChange={e => { void loadInstrumentFile(e.target.files?.[0]); e.target.value = ''; }} />
      </div>
      {showYaml && <pre className="stb-yaml">{yamlText}</pre>}
      {legacy && (
        <div className="stb-dt-legacy">
          <div className="sd-grid sd-grid--side sd-grid--square stb-dt-name">
            <div className="sd-row">
              <label className="sd-label" htmlFor="stb-dt-name">Name<span className="req">*</span></label>
              <input id="stb-dt-name" className={`sd-input${editable ? '' : ' readonly'}`} readOnly={!editable} maxLength={50}
                value={name} onChange={e => setName(e.target.value)} />
            </div>
          </div>
          <fieldset className="stb-dt-mm">
            <legend>Mail Merge</legend>
            <div className="sd-grid sd-grid--side sd-grid--square stb-dt-ranges">
              {([['HEADERRANGE', 'Header Range'], ['CHEADER', 'CHeader Range'], ['DETAILRANGE', 'Detail Range'], ['CDETAIL', 'CDetail Range']] as const).map(([band, label]) => (
                <div className="sd-row" key={band}>
                  <label className="sd-label" htmlFor={`stb-dt-${band}`}>{label}</label>
                  <input id={`stb-dt-${band}`} className={`sd-input${editable ? '' : ' readonly'}`} readOnly={!editable}
                    placeholder="e.g. A1:F2" value={rangeOf(band)} onChange={e => typeRange(band, e.target.value)} />
                </div>
              ))}
            </div>
          </fieldset>
        </div>
      )}
      <div className="stb-sheetbar" data-unsaved-ignore hidden={legacy}>
        <span className="stb-dt-bands">
          <i className="ti ti-layout-rows" /> Mail merge:
          {allBands.filter(b => (TRANSFER_BANDS as readonly string[]).includes(b.name)).length === 0
            && <span className="stb-hint">no Header / Detail band set</span>}
          {allBands.filter(b => (TRANSFER_BANDS as readonly string[]).includes(b.name)).map(b => (
            <span key={`${b.sheetIndex}-${b.name}`} className={`stb-dt-band stb-dt-band--${b.name === 'HEADERRANGE' ? 'h' : 'd'}`}>
              {BAND_LABEL[b.name]}: {sheetName(b.sheetIndex)}!{b.range}
              {editable && <button type="button" aria-label={`Clear ${BAND_LABEL[b.name]} band`} title="Clear this band"
                onClick={() => clearBand(b.sheetIndex, b.name as (typeof TRANSFER_BANDS)[number])}><i className="ti ti-x" /></button>}
            </span>
          ))}
        </span>
        <span className="stb-sheetbar-actions">
          <button type="button" className="ca-btn" disabled={!editable || !ready} onClick={() => setBandFromSelection('HEADERRANGE')}>Set Header</button>
          <button type="button" className="ca-btn" disabled={!editable || !ready} onClick={() => setBandFromSelection('DETAILRANGE')}>Set Detail</button>
        </span>
      </div>
      <div className="stb-sheet-row">
        {legacy ? (
          <aside className="stb-fieldlist stb-dt-lists" aria-label="Field lists">
            {([['RawData', fields], ['CalibrationData', calibrationFields]] as const).map(([title, list]) => (
              <div className="stb-dt-list" key={title}>
                <div className="stb-dt-list-title">{title}:</div>
                <div className="stb-fieldlist-head">FieldName</div>
                <ul className="stb-fieldlist-items">
                  {list.length === 0 && <li className="stb-fieldlist-empty">No data to display</li>}
                  {list.map(f => (
                    <li key={f.name} draggable={editable} tabIndex={0}
                      className={mapped.has(f.name) ? 'mapped' : undefined}
                      title={`${f.caption ?? f.name}\nDrag onto a cell, or press Enter / double-click to put it in the selected cell.`}
                      onDragStart={e => { e.dataTransfer.setData(FIELD_DRAG_MIME, f.name); e.dataTransfer.effectAllowed = 'copy'; }}
                      onDoubleClick={() => { if (editable) putFieldAtSelection(f.name); }}
                      onKeyDown={e => { if (e.key === 'Enter' && editable) { e.preventDefault(); putFieldAtSelection(f.name); } }}>
                      <span className="stb-fieldlist-name">{f.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </aside>
        ) : (
        <aside className="stb-fieldlist" aria-label="Field List">
          <div className="stb-fieldlist-title" title="SpreadSheetDataSource">
            <i className="ti ti-database" /> RawDataTableDataSource
          </div>
          <div className="stb-fieldlist-search">
            <input type="text" className="sd-input" placeholder="Search fields…" value={fieldSearch}
              onChange={e => setFieldSearch(e.target.value)} data-unsaved-ignore aria-label="Search fields" />
          </div>
          {fields.length === 0
            ? <p className="stb-hint stb-fieldlist-empty">No fields yet — add them on the Field Info tab.</p>
            : (
              <ul className="stb-fieldlist-items">
                {visibleFields.map(f => (
                  <li key={f.name} draggable={editable} tabIndex={0}
                    className={mapped.has(f.name) ? 'mapped' : undefined}
                    title={`${f.caption ?? f.name}\nDrag onto a cell, or press Enter / double-click to put it in the selected cell.`}
                    onDragStart={e => { e.dataTransfer.setData(FIELD_DRAG_MIME, f.name); e.dataTransfer.effectAllowed = 'copy'; }}
                    onDoubleClick={() => { if (editable) putFieldAtSelection(f.name); }}
                    onKeyDown={e => { if (e.key === 'Enter' && editable) { e.preventDefault(); putFieldAtSelection(f.name); } }}>
                    <span className="stb-fieldlist-name">{f.name}</span>
                    {f.caption && f.caption.toUpperCase() !== f.name && <span className="stb-fieldlist-cap">{f.caption}</span>}
                  </li>
                ))}
              </ul>
            )}
        </aside>
        )}
        <div className="xlwrap xluniver stb-outputs-viewer stb-sheet">
          {book ? (
            <Suspense fallback={<div className="xl-loading">Loading spreadsheet…</div>}>
              <TemplateSheetDesigner ref={sheetRef} buf={book.buf} sheetNames={book.info.sheets} formulas={book.info.formulas}
                readOnly={!editable} onReady={() => setReady(true)} onFieldDropped={(at, name) => putField(name, at)} />
            </Suspense>
          ) : <div className="xl-loading">Loading…</div>}
        </div>
      </div>
    </AppDialog>
  );
}
